<template>
  <modal
    v-if="isVisible"
    elem-id="assistant-franchise-modal"
    :show="isVisible"
    :title="t('Selecionar franquia')"
    :close-label="t('Fechar')"
    :save-fn="null"
    size="small"
    close-test-id="assistant-franchise-modal-close"
    @close="handleClose"
    @dismiss="handleClose"
  >
    <div data-testid="assistant-franchise-modal">
      <ul class="assistant-franchise-list" role="listbox" :aria-label="t('Franquias disponíveis')">
        <li
          v-for="franquia in franquias"
          :key="franquia.ID"
          class="assistant-franchise-option"
          :class="{
            'assistant-franchise-option--selected': Number(franquia.ID) === selectedFranquiaId,
            'assistant-franchise-option--disabled': isZeroBalance(Number(franquia.ID)),
          }"
          role="option"
          :aria-selected="Number(franquia.ID) === selectedFranquiaId"
          :aria-disabled="isZeroBalance(Number(franquia.ID))"
          :data-testid="`assistant-franchise-option`"
          :data-testid-franquia-id="franquia.ID"
          @click="handleSelect(Number(franquia.ID))"
        >
          <span class="assistant-franchise-option__name">{{ franquia.Name }}</span>
          <span class="assistant-franchise-option__balance">
            <template v-if="loadingBalances">
              <i class="pi pi-spin pi-spinner assistant-franchise-option__balance-spinner" aria-hidden="true" />
            </template>
            <template v-else>
              {{ humanizedBalanceFor(Number(franquia.ID)) }}
            </template>
          </span>
          <i
            v-if="Number(franquia.ID) === selectedFranquiaId"
            class="pi pi-check assistant-franchise-option__check"
            aria-hidden="true"
          />
        </li>
      </ul>
    </div>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { permissionsService } from '../../services/permissionsService'
import { useCreditWallet } from '../../composables/useCreditWallet'
import { useFranchiseBalances } from '../../composables/useFranchiseBalances'
import { formatCredits } from './formatCredits'
import { eventBus } from '../../eventBus'
import { log } from '../../utils/DebugLogger'

const { t } = useI18n()

const isVisible = ref<boolean>(false)
const loadingBalances = ref<boolean>(false)

const { selectedFranquiaId, fetchWallet } = useCreditWallet()
const { balances, fetchBalances } = useFranchiseBalances()

const franquias = computed(() => permissionsService.getFranquiasSelectArray())

/**
 * Humanized per-franchise balance in credits (e.g. '5 cr'), derived from
 * balance_micro — the backend's balance_display is a RAW 6-decimal string
 * and must never be rendered. Shows '—' when the fetch for that franchise
 * failed (ok: false) or the entry is absent.
 * Shows a spinner at the row level while loadingBalances is true.
 */
function humanizedBalanceFor(id: number): string {
  const entry = balances.value.get(id)
  if (!entry) return '—'
  if (!entry.ok) return '—'
  return `${formatCredits(entry.micro)} ${t('cr')}`
}

/**
 * A franchise with a KNOWN zero/negative balance is not selectable.
 * Unknown balances (fetch failed or still loading) stay selectable — blocking
 * on a transient fetch error would lock the user out, and fetchWallet on
 * select resolves the truth anyway (the 402 banner handles insufficiency).
 */
function isZeroBalance(id: number): boolean {
  const entry = balances.value.get(id)
  return entry?.ok === true && entry.micro <= 0
}

async function handleOpen(): Promise<void> {
  log('AssistantFranchiseModal', 'handleOpen', 'Opening franchise selector')
  isVisible.value = true
  const ids = franquias.value.map((f) => Number(f.ID))
  if (ids.length > 0) {
    loadingBalances.value = true
    try {
      await fetchBalances(ids)
    } finally {
      loadingBalances.value = false
    }
  }
}

function handleClose(): void {
  log('AssistantFranchiseModal', 'handleClose', 'Closing franchise selector')
  isVisible.value = false
}

async function handleSelect(id: number): Promise<void> {
  if (loadingBalances.value || isZeroBalance(id)) {
    log('AssistantFranchiseModal', 'handleSelect', 'Seleção bloqueada (sem créditos ou carregando)', { id })
    return
  }
  log('AssistantFranchiseModal', 'handleSelect', 'Franquia selecionada', { id })
  selectedFranquiaId.value = id
  await fetchWallet(id)
  handleClose()
}

onMounted(() => {
  eventBus.on('modal.assistant-franchise.open', handleOpen)
  eventBus.on('modal.assistant-franchise.close', handleClose)
})

onBeforeUnmount(() => {
  eventBus.off('modal.assistant-franchise.open', handleOpen)
  eventBus.off('modal.assistant-franchise.close', handleClose)
})
</script>

<style scoped>
.assistant-franchise-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  /* P0 fix #3: bounded height with scroll so long lists don't overflow modal */
  max-height: 22rem;
  overflow-y: auto;
  background: var(--p-surface-0, #ffffff);
  border-radius: 0.5rem;
}

.assistant-franchise-list::-webkit-scrollbar {
  width: 4px;
}

.assistant-franchise-list::-webkit-scrollbar-track {
  background: transparent;
}

.assistant-franchise-list::-webkit-scrollbar-thumb {
  background: var(--p-surface-300, #cbd5e1);
  border-radius: 2px;
}

.assistant-franchise-option {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0.875rem;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: background 0.15s ease;
  font-size: 0.875rem;
  color: var(--p-text-color);
  /* P0 fix #3: each row is fully opaque — no bleed-through */
  background: var(--p-surface-0, #ffffff);
}

.assistant-franchise-option:hover {
  background: var(--p-surface-100, #f1f5f9);
}

.assistant-franchise-option--selected {
  background: var(--p-primary-50, #eff6ff);
  color: var(--p-primary-color);
  font-weight: 600;
}

.assistant-franchise-option--selected:hover {
  background: var(--p-primary-100, #dbeafe);
}

.assistant-franchise-option--disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.assistant-franchise-option--disabled:hover {
  background: var(--p-surface-0, #ffffff);
}

.assistant-franchise-option__name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.assistant-franchise-option__balance {
  white-space: nowrap;
  font-size: 0.8125rem;
  color: var(--p-text-muted-color, #64748b);
  flex-shrink: 0;
  min-width: 3.5rem;
  text-align: right;
}

.assistant-franchise-option__balance-spinner {
  font-size: 0.75rem;
  color: var(--p-text-muted-color, #64748b);
}

.assistant-franchise-option--selected .assistant-franchise-option__balance {
  color: var(--p-primary-color);
}

.assistant-franchise-option__check {
  font-size: 0.75rem;
  flex-shrink: 0;
}
</style>
